import config from 'src/config/runtimeConfig';
import deviceCommon from 'src/http/DeviceCommon';
import { getToken } from 'src/utils/tokenStore';

class FetchClient {
  constructor() {
    this._baseURL = config.REACT_APP_API_URL;
    this._onUnauthorized = null;
  }

  setOnUnauthorized(fn) {
    this._onUnauthorized = fn;
  }

  _buildHeaders(callerHeaders) {
    const headers = {};

    headers['X-DEVICE-ID'] = deviceCommon.getDeviceId();

    const accessToken = getToken();
    if (accessToken) {
      headers.AccessToken = `Bearer ${accessToken}`;
    }

    const activeProviderId = localStorage.getItem('activeProviderId');
    if (activeProviderId) {
      headers['X-PROVIDER-ID'] = activeProviderId;
    }

    const activePartnerId = localStorage.getItem('activePartnerId');
    if (activePartnerId) {
      headers['X-PARTNER-ID'] = activePartnerId;
    }

    const activeDistributorId = localStorage.getItem('activeDistributorId');
    if (activeDistributorId) {
      headers['X-DISTRIBUTOR-ID'] = activeDistributorId;
    }

    if (callerHeaders) {
      Object.assign(headers, callerHeaders);
    }

    return headers;
  }

  _buildURL(path, params) {
    const url = `${this._baseURL.replace(/\/$/, '')}${path}`;

    if (!params || Object.keys(params).length === 0) {
      return url;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value);
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `${url}?${queryString}` : url;
  }

  async _request(method, path, { data, params, headers: callerHeaders, responseType } = {}) {
    const url = this._buildURL(path, params);
    const headers = this._buildHeaders(callerHeaders);

    const fetchOptions = { method, headers, credentials: 'include' };

    const upperMethod = method.toUpperCase();
    if (upperMethod !== 'GET' && data !== undefined) {
      if (data instanceof FormData) {
        fetchOptions.body = data;
        delete fetchOptions.headers['Content-Type'];
      } else {
        fetchOptions.body = JSON.stringify(data);
      }
    }

    const response = await fetch(url, fetchOptions);

    let responseBody;
    if (responseType === 'blob') {
      responseBody = await response.blob();
    } else {
      try {
        responseBody = await response.json();
      } catch {
        responseBody = null;
      }
    }

    if (!response.ok) {
      const responseHeaders = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });

      if (response.status === 401 && this._onUnauthorized) {
        this._onUnauthorized();
      }

      const error = new Error(`Request failed with status ${response.status}`);
      error.response = { data: responseBody, status: response.status, headers: responseHeaders };
      throw error;
    }

    const responseHeaders = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    return { data: responseBody, status: response.status, headers: responseHeaders };
  }

  get(url, options) {
    return this._request('GET', url, options);
  }

  post(url, data, options) {
    return this._request('POST', url, { ...options, data });
  }

  put(url, data, options) {
    return this._request('PUT', url, { ...options, data });
  }

  delete(url, options) {
    return this._request('DELETE', url, options);
  }

  patch(url, data, options) {
    return this._request('PATCH', url, { ...options, data });
  }
}

const fetchClient = new FetchClient();

export default fetchClient;
